import { useEffect, useState } from "react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Reveal, SectionTag } from "./shared";
import { useLang } from "./i18n";

const FAQS = {
  en: [
    { q: "How much does Bright IQ cost?", a: "Early access is completely free — join the list and your first insight report is on us. At launch, individual reports will be priced per assessment, with discounted plans for schools and counselors managing whole cohorts." },
    { q: "How long does the full assessment take?", a: "About 45 minutes, adaptive to your pace. You can pause and resume anytime. Your counselor-reviewed report arrives within 48 hours of completion." },
    { q: "Who actually reviews my report?", a: "Every report is drafted by our AI from your scores, then reviewed and signed off by a certified career counselor before it reaches you. No report goes out unreviewed — that's a promise, not a feature flag." },
    { q: "Is my data private?", a: "Yes. Your responses and scores are encrypted, never sold, and never shared with schools, parents or anyone else without your explicit consent. You can request full deletion of your data at any time." },
    { q: "What ages is Bright IQ designed for?", a: "Students from Class 8 through early college (roughly ages 13–22). The assessment calibrates question difficulty and language to your grade level automatically." },
    { q: "Can my school or coaching institute use it?", a: "Absolutely. The counselor dashboard lets institutions run cohort-wide assessments, compare clusters, and spot students who need guidance early. Reach out via the early-access list and mention your institution." },
  ],
  hi: [
    { q: "Bright IQ की कीमत क्या है?", a: "अर्ली एक्सेस बिल्कुल मुफ़्त है — सूची से जुड़ें और आपकी पहली इनसाइट रिपोर्ट हमारी ओर से है। लॉन्च पर रिपोर्ट प्रति आकलन के हिसाब से होंगी, और स्कूलों/काउंसलरों के लिए रियायती प्लान होंगे।" },
    { q: "पूरा आकलन कितने समय का होता है?", a: "लगभग 45 मिनट, आपकी गति के अनुसार। आप बीच में रोककर बाद में जारी कर सकते हैं। काउंसलर-समीक्षित रिपोर्ट पूरा होने के 48 घंटों के भीतर मिलती है।" },
    { q: "मेरी रिपोर्ट असल में कौन देखता है?", a: "हर रिपोर्ट आपके स्कोर से AI द्वारा तैयार होती है, फिर प्रमाणित करियर काउंसलर उसकी समीक्षा और अनुमोदन करता है। बिना समीक्षा कोई रिपोर्ट नहीं भेजी जाती।" },
    { q: "क्या मेरा डेटा निजी है?", a: "हाँ। आपके जवाब और स्कोर सुरक्षित रखे जाते हैं, कभी बेचे नहीं जाते, और आपकी स्पष्ट सहमति के बिना स्कूल, अभिभावक या किसी और के साथ साझा नहीं किए जाते। आप कभी भी डेटा हटाने का अनुरोध कर सकते हैं।" },
    { q: "Bright IQ किस उम्र के लिए बना है?", a: "कक्षा 8 से कॉलेज की शुरुआती सालों तक (लगभग 13–22 वर्ष)। आकलन आपकी कक्षा के अनुसार प्रश्नों की कठिनाई और भाषा स्वतः समायोजित करता है।" },
    { q: "क्या मेरा स्कूल या कोचिंग इसे इस्तेमाल कर सकता है?", a: "बिल्कुल। काउंसलर डैशबोर्ड से संस्थान पूरे समूह का आकलन कर सकते हैं, क्लस्टर तुलना कर सकते हैं और जल्दी मार्गदर्शन चाहने वाले विद्यार्थियों को पहचान सकते हैं।" },
  ],
};

export default function FAQ() {
  const { lang, t } = useLang();
  const [cmsFaqs, setCmsFaqs] = useState(null);
  const faqs = lang === "hi" ? FAQS.hi : (cmsFaqs || FAQS.en);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content/faqs`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => { if (Array.isArray(d) && d.length) setCmsFaqs(d); })
      .catch(() => {});
  }, []);

  return (
    <section id="faq" className="max-w-4xl mx-auto px-5 sm:px-8 py-28 sm:py-36">
      <SectionTag color="#17B6E0">{t.faqTag}</SectionTag>
      <Reveal>
        <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight text-balance leading-[1.05]">
          {t.faqHeadA} <span className="font-serifa italic font-normal text-iqcyan">{t.faqHeadB}</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-ink/10" data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="font-display font-semibold text-base sm:text-lg text-left tracking-tight hover:no-underline hover:text-iqblue transition-colors py-6">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm sm:text-base text-ink/60 leading-relaxed pb-6 max-w-2xl">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </section>
  );
}
